import { MessageSort } from '@api'
import { combine, createEvent, createStore, sample } from 'effector'
import { matchPath } from 'react-router'

import { Pages } from '@/consts/routes.ts'
import { $currentPage } from '@/models/router.ts'
import {
  compareTimes,
  getDateWithHoursAndMinutesFromTimeObject,
  getLongestFreeInterval,
  parseTimeStringToTimObject,
} from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import type { WelcomeMessage } from '@/modules/creator-settings/types.ts'
import {
  $followUpMessages,
  $sendAfterMinutes,
  $sendIfNotSeenParentMessage,
} from '@/modules/message/models.ts'
import { Message } from '@/modules/message/service/message-service.tsx'
import { areMessagesDifferent } from '@/modules/message/utils.ts'

export const emptyArray: any[] = []

export const setSortOption = createEvent<MessageSort>('set sort option')
export const $welcomeMessageSortOptions = createStore<MessageSort>(
  MessageSort.DEFAULT,
).on(setSortOption, (_, sortOption) => sortOption)

export const setWelcomeMessage = createEvent<Partial<WelcomeMessage> | null>(
  'set welcome message',
)
export const $welcomeMessage = createStore<Partial<WelcomeMessage> | null>(
  null,
  {
    name: 'welcome message',
  },
).on(setWelcomeMessage, (_, value) => value)

export const setWelcomeMessages = createEvent<WelcomeMessage[]>(
  'set welcome messages list',
)
export const $welcomeMessages = createStore<WelcomeMessage[]>(emptyArray, {
  name: 'welcome messages list',
}).on(setWelcomeMessages, (_, value) => value)

export const $bookedIntervals = combine(
  $welcomeMessages,
  $welcomeMessage,
  (messages, data) => {
    return messages
      .filter((m) => m.id !== data?.id && m.timeframeStart && m.timeframeEnd)
      .sort((a, b) => compareTimes(a.timeframeStart, b.timeframeStart))
      .map((message) => ({
        start: message.timeframeStart,
        end: message.timeframeEnd,
      }))
  },
)

export const $longestFreeTimeframe = $bookedIntervals.map(
  getLongestFreeInterval,
)

export const addMessage = createEvent<Message>('add welcome form message')
export const updateMessage = createEvent<Message>('update welcome form message')
export const removeMessage = createEvent<{ id: Message['id'] }>(
  'remove welcome form message',
)
export const $messages = createStore<Message[]>(emptyArray, {
  name: 'welcome form messages',
})
  .on(
    setWelcomeMessage,
    (_, payload) => payload?.sequence?.messages ?? emptyArray,
  )
  .on(addMessage, (messages, message) => [
    ...messages,
    { ...message, id: messages.length },
  ])
  .on(updateMessage, (messages, message) =>
    messages.map((m) => (m.id === message.id ? message : m)),
  )
  .on(removeMessage, (messages, { id }) => messages.filter((m) => m.id !== id))

export const setExcludeList = createEvent<string[] | undefined>(
  'set welcome form exclude list',
)
export const $excludeList = createStore<string[]>(emptyArray, {
  name: 'welcome form exclude list',
})
  .on(setWelcomeMessage, (_, data) =>
    (data?.excludedTrackedLinks ?? emptyArray).map((id) => String(id)),
  )
  .on(setExcludeList, (_, excludeList) => excludeList ?? emptyArray)

export const setIncludeList = createEvent<string[] | undefined>(
  'set welcome form include list',
)
export const $includeList = createStore<string[]>(emptyArray, {
  name: 'welcome form include list',
})
  .on(setWelcomeMessage, (_, data) =>
    (data?.includedTrackedLinks ?? emptyArray).map((id) => String(id)),
  )
  .on(setIncludeList, (_, includeList) => includeList ?? emptyArray)

export const DEFAULT_FROM_DATE = new Date()
DEFAULT_FROM_DATE.setHours(0, 0)

export const DEFAULT_TO_DATE = new Date()
DEFAULT_TO_DATE.setHours(23, 59)

export const setTimeframe = createEvent<{ fromTime: Date; toTime: Date }>(
  'set welcome form timeframe',
)

export const $fromTime = createStore(DEFAULT_FROM_DATE, {
  name: 'welcome form `from time`',
}).on(setTimeframe, (_, { fromTime }) => fromTime)

export const $toTime = createStore(DEFAULT_TO_DATE, {
  name: 'welcome form `to time`',
}).on(setTimeframe, (_, { toTime }) => toTime)

export const setFollowUpEnabled = createEvent<boolean>(
  'set welcome form follow up enabled',
)
export const $followUpEnabled = createStore(false, {
  name: 'welcome form follow up enabled',
})
  .on(setWelcomeMessage, (_, data) => !!data?.followUpEnabled)
  .on(setFollowUpEnabled, (_, value) => value)

export const toggleWithoutTimeframe = createEvent<unknown>(
  'toggle welcome form without timeframe',
)
export const $withoutTimeframe = createStore(false, {
  name: 'welcome form without timeframe',
})
  .on(
    setWelcomeMessage,
    (_, data) => !!data && !data?.timeframeStart && !data?.timeframeEnd,
  )
  .on(toggleWithoutTimeframe, (value) => !value)

export const $initialValues = combine(
  $welcomeMessage,
  $longestFreeTimeframe,
  (message, longestFreeTimeframe) => {
    return {
      messages: message?.sequence?.messages ?? (emptyArray as Array<Message>),
      excludeList: (message?.excludedTrackedLinks ?? emptyArray).map((id) =>
        String(id),
      ),
      includeList: (message?.includedTrackedLinks ?? emptyArray).map((id) =>
        String(id),
      ),
      fromTime: message
        ? getDateWithHoursAndMinutesFromTimeObject(
            parseTimeStringToTimObject(
              message.timeframeStart || longestFreeTimeframe.start,
            ),
          )
        : DEFAULT_FROM_DATE,
      toTime: message
        ? getDateWithHoursAndMinutesFromTimeObject(
            parseTimeStringToTimObject(
              message.timeframeEnd || longestFreeTimeframe.end,
            ),
          )
        : DEFAULT_TO_DATE,
      followUpEnabled: !!message?.followUpEnabled,
      sendAfterMinutes: message?.followUpDto?.sendAfterMinutes,
      sendIfNotSeenParentMessage:
        message?.followUpDto?.sendIfNotSeenParentMessage,
      followUpMessages:
        message?.followUpDto?.sequence?.messages ??
        (emptyArray as Array<Message>),
      withoutTimeframe:
        !!message && !message?.timeframeStart && !message?.timeframeEnd,
    }
  },
)

// better to use `react-hook-form` to handle it
export const $isFormDirty = combine(
  $initialValues,
  $messages,
  $excludeList,
  $includeList,
  $fromTime,
  $toTime,
  $followUpEnabled,
  $sendAfterMinutes,
  $sendIfNotSeenParentMessage,
  $followUpMessages,
  $withoutTimeframe,
  (
    initialValues,
    messages,
    excludeList,
    includeList,
    fromTime,
    toTime,
    followUpEnabled,
    sendAfterMinutes,
    sendIfNotSeenParentMessage,
    followUpMessages,
    withoutTimeframe,
  ) => {
    return (
      areMessagesDifferent(initialValues.messages, messages) ||
      JSON.stringify(excludeList) !==
        JSON.stringify(initialValues.excludeList) ||
      JSON.stringify(includeList) !==
        JSON.stringify(initialValues.includeList) ||
      fromTime !== initialValues.fromTime ||
      toTime !== initialValues.toTime ||
      withoutTimeframe !== initialValues.withoutTimeframe ||
      followUpEnabled !== initialValues.followUpEnabled ||
      // track follow-up dto state only for enabled follow-ups in WM
      (initialValues.followUpEnabled &&
        (initialValues.sendAfterMinutes !== sendAfterMinutes ||
          initialValues.sendIfNotSeenParentMessage !==
            sendIfNotSeenParentMessage ||
          areMessagesDifferent(
            initialValues.followUpMessages,
            followUpMessages,
          )))
    )
  },
)

// update from\till dates on message update
sample({
  source: {
    initialValues: $initialValues,
  },
  fn: ({ initialValues: { fromTime, toTime } }) => {
    return { fromTime, toTime }
  },
  target: setTimeframe,
})

// reset model when we leave create\edit pages
sample({
  clock: $currentPage,
  filter: (page) => {
    const isWelcomeMessagesCreatePage = matchPath(
      Pages.WELCOME_MESSAGES_CREATE,
      page,
    )
    const isWelcomeMessagesEditPage = matchPath(
      Pages.WELCOME_MESSAGES_EDIT,
      page,
    )

    return !isWelcomeMessagesEditPage && !isWelcomeMessagesCreatePage
  },
  fn: () => null,
  target: setWelcomeMessage,
})
